/**
 * Admin UI for Training Rules Engine
 * 
 * Training Framework v1 baseline behavior is defined by the current defaults in RulesEngine.
 * This UI only surfaces adjustments; it does not change the v1 defaults in code.
 * 
 * Rule Reference Table:
 * | rule_name                  | type       | range      | description                                    |
 * |----------------------------|------------|------------|------------------------------------------------|
 * | volume_progression_rate    | float      | 0.05-0.15  | Weekly volume increase percentage              |
 * | long_run_percentage        | float      | 0.20-0.40  | Long run as percentage of weekly volume        |
 * | longest_run_spike_limit    | float      | 0.20-0.50  | Max increase from current longest run          |
 * | quality_session_frequency  | int        | 2-4        | Maximum hard days per week                     |
 * | recovery_run_pace_offset   | int        | 10-20      | Recovery pace offset in sec/km                 |
 * | no_back_to_back_hard_days  | bool       | true/false | Prevent consecutive hard training days         |
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../../utils/apiUtils';

// Admin whitelist check (same pattern as AdminFeedbackPage)
const RAW_ALLOWLIST = (process.env.REACT_APP_ADMIN_EMAIL_WHITELIST || '')
  .split(',')
  .map((value) => value.trim().toLowerCase())
  .filter(Boolean);

const ADMIN_EMAILS = RAW_ALLOWLIST.filter((entry) => entry.includes('@'));
const ADMIN_IDS = RAW_ALLOWLIST.filter((entry) => !entry.includes('@'));

// Rule definitions with display metadata
const RULE_DEFINITIONS = [
  {
    name: 'volume_progression_rate',
    label: 'Volume Progression Rate',
    description: 'Weekly volume increase percentage. Higher values increase injury risk.',
    type: 'float',
    min: 0.05,
    max: 0.15,
    step: 0.01,
    displayMultiplier: 100,
    displaySuffix: '%',
    recommendation: '5-10% for beginners, 10-15% for advanced'
  },
  {
    name: 'long_run_percentage',
    label: 'Long Run Percentage',
    description: 'Long run as percentage of weekly volume.',
    type: 'float',
    min: 0.20,
    max: 0.40,
    step: 0.01,
    displayMultiplier: 100,
    displaySuffix: '%',
    recommendation: 'Marathoners may use up to 40%'
  },
  {
    name: 'longest_run_spike_limit',
    label: 'Longest Run Spike Limit',
    description: 'Maximum increase from current longest run.',
    type: 'float',
    min: 0.20,
    max: 0.50,
    step: 0.01,
    displayMultiplier: 100,
    displaySuffix: '%',
    recommendation: '30% is conservative, 50% is aggressive'
  },
  {
    name: 'quality_session_frequency',
    label: 'Maximum Hard Days Per Week',
    description: 'Maximum hard days (quality workouts + long run) per week.',
    type: 'int',
    min: 2,
    max: 4,
    step: 1,
    displayMultiplier: 1,
    displaySuffix: ' days',
    recommendation: '2-3 recommended for most athletes'
  },
  {
    name: 'recovery_run_pace_offset',
    label: 'Recovery Pace Offset',
    description: 'How much slower than easy pace for recovery runs.',
    type: 'int',
    min: 10,
    max: 20,
    step: 1,
    displayMultiplier: 1,
    displaySuffix: ' sec/km',
    recommendation: 'Higher = easier recovery runs. 10 sec/km ≈ 16 sec/mi, 20 sec/km ≈ 32 sec/mi'
  },
  {
    name: 'no_back_to_back_hard_days',
    label: 'No Back-to-Back Hard Days',
    description: 'Prevent consecutive hard training days.',
    type: 'bool',
    recommendation: 'Critical for injury prevention',
    isProtected: true  // Requires confirmation to change
  }
];

const API_BASE = getApiUrl();
const RUNBOOK_PATH = '/docs/training_framework_v1_runbook.md';

const TrainingRulesAdmin = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const primaryEmail = user?.primaryEmailAddress?.emailAddress?.toLowerCase();
  const clerkId = user?.id?.toLowerCase();

  const isConfigured = RAW_ALLOWLIST.length > 0;
  const isAuthorized = useMemo(() => {
    if (!isConfigured || !(primaryEmail || clerkId)) {
      return false;
    }
    return ADMIN_EMAILS.includes(primaryEmail) || ADMIN_IDS.includes(clerkId);
  }, [primaryEmail, clerkId, isConfigured]);

  const [ruleStates, setRuleStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});
  const [messages, setMessages] = useState({});
  const [historyModal, setHistoryModal] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [saveConfirmModal, setSaveConfirmModal] = useState(null);

  const fetchAuthHeaders = useCallback(async () => {
    const token = await getToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, [getToken]);

  // Load all rules on mount (including last changed info from history)
  useEffect(() => {
    const loadRules = async () => {
      if (!isAuthorized) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const headers = await fetchAuthHeaders();
        const results = {};
        
        for (const rule of RULE_DEFINITIONS) {
          try {
            // Fetch rule config
            const response = await fetch(`${API_BASE}/admin/rules/${rule.name}`, { headers });
            if (response.ok) {
              const data = await response.json();
              
              // Fetch last history entry for "last changed" info
              let lastChanged = null;
              try {
                const historyResponse = await fetch(`${API_BASE}/admin/rules/${rule.name}/history?limit=1`, { headers });
                if (historyResponse.ok) {
                  const historyData = await historyResponse.json();
                  if (historyData.length > 0) {
                    lastChanged = {
                      timestamp: historyData[0].updated_at,
                      updatedBy: historyData[0].updated_by
                    };
                  }
                }
              } catch (e) {
                console.error(`Failed to load history for ${rule.name}:`, e);
              }
              
              results[rule.name] = {
                currentValue: data.current_resolved,
                defaultValue: data.developer?.value,
                adminValue: data.admin?.value,
                adminEnabled: data.admin?.enabled,
                version: data.admin?.version,
                editValue: data.current_resolved,
                metadata: data.developer?.metadata,
                historyCount: data.admin?.version || 0,
                lastChanged
              };
            }
          } catch (e) {
            console.error(`Failed to load rule ${rule.name}:`, e);
          }
        }
        
        setRuleStates(results);
      } catch (e) {
        setError('Failed to load rules');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    
    loadRules();
  }, [isAuthorized, fetchAuthHeaders]);

  const handleValueChange = (ruleName, value) => {
    setRuleStates(prev => ({
      ...prev,
      [ruleName]: {
        ...prev[ruleName],
        editValue: value
      }
    }));
    // Clear any existing message
    setMessages(prev => ({ ...prev, [ruleName]: null }));
  };

  const handleSave = async (ruleName, skipConfirm = false, skipSaveConfirm = false) => {
    const rule = RULE_DEFINITIONS.find(r => r.name === ruleName);
    const state = ruleStates[ruleName];
    if (!rule || !state) return;

    // For protected rules, require special confirmation first
    if (rule.isProtected && !skipConfirm) {
      setConfirmModal(ruleName);
      setConfirmInput('');
      return;
    }

    setSaving(prev => ({ ...prev, [ruleName]: true }));
    setMessages(prev => ({ ...prev, [ruleName]: null }));

    try {
      const headers = await fetchAuthHeaders();
      
      // Step 1: Validate (ALWAYS validate before save)
      const validateResponse = await fetch(`${API_BASE}/admin/rules/${ruleName}/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ rule_value: state.editValue })
      });
      
      const validateResult = await validateResponse.json();
      
      if (!validateResult.is_valid) {
        setMessages(prev => ({
          ...prev,
          [ruleName]: { type: 'error', text: validateResult.errors?.join(', ') || 'Validation failed' }
        }));
        setSaving(prev => ({ ...prev, [ruleName]: false }));
        return; // Do NOT persist if validation fails
      }
      
      // Step 2: Show confirmation modal (unless already confirmed)
      if (!skipSaveConfirm) {
        setSaveConfirmModal(ruleName);
        setSaving(prev => ({ ...prev, [ruleName]: false }));
        return;
      }
      
      // Step 3: Save (only if validation passed AND user confirmed)
      const saveResponse = await fetch(`${API_BASE}/admin/rules/${ruleName}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rule_value: state.editValue,
          enabled: true
        })
      });
      
      if (!saveResponse.ok) {
        throw new Error('Failed to save');
      }
      
      const saveResult = await saveResponse.json();
      
      // Update state with new value
      setRuleStates(prev => ({
        ...prev,
        [ruleName]: {
          ...prev[ruleName],
          currentValue: state.editValue,
          adminValue: state.editValue,
          version: saveResult.version,
          historyCount: saveResult.version,
          lastChanged: {
            timestamp: new Date().toISOString(),
            updatedBy: null // We don't have access to user ID here
          }
        }
      }));
      
      setMessages(prev => ({
        ...prev,
        [ruleName]: { type: 'success', text: 'Saved successfully' }
      }));
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessages(prev => ({ ...prev, [ruleName]: null }));
      }, 3000);
      
    } catch (e) {
      setMessages(prev => ({
        ...prev,
        [ruleName]: { type: 'error', text: e.message || 'Failed to save' }
      }));
    } finally {
      setSaving(prev => ({ ...prev, [ruleName]: false }));
    }
  };

  const handleConfirmSave = () => {
    if (confirmInput === 'DISABLE SAFETY') {
      // For protected rules, go to save confirmation
      setSaveConfirmModal(confirmModal);
      setConfirmModal(null);
      setConfirmInput('');
    }
  };

  const handleSaveConfirm = () => {
    handleSave(saveConfirmModal, true, true);
    setSaveConfirmModal(null);
  };

  const handleShowHistory = async (ruleName) => {
    setHistoryModal(ruleName);
    setHistoryLoading(true);
    setHistoryData([]);
    
    try {
      const headers = await fetchAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/rules/${ruleName}/history?limit=10`, { headers });
      if (response.ok) {
        const data = await response.json();
        setHistoryData(data);
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Helper to format display value
  const formatDisplayValue = (value, rule) => {
    if (value === undefined || value === null) return 'N/A';
    if (rule.type === 'bool') {
      return value ? 'Enabled' : 'Disabled';
    }
    const displayVal = (value * (rule.displayMultiplier || 1)).toFixed(rule.type === 'int' ? 0 : 0);
    return `${displayVal}${rule.displaySuffix || ''}`;
  };

  // Helper to format last changed info
  const formatLastChanged = (lastChanged) => {
    if (!lastChanged || !lastChanged.timestamp) {
      return 'never (using developer default)';
    }
    const date = new Date(lastChanged.timestamp);
    const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    return dateStr;
  };

  // Access control checks
  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-200 flex items-center justify-center px-6">
        <div className="max-w-xl space-y-4 text-center">
          <p className="text-2xl font-semibold text-white">Admin access not configured</p>
          <p className="text-gray-400">
            Set <code className="text-blue-300">REACT_APP_ADMIN_EMAIL_WHITELIST</code> to enable admin access.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-200 flex items-center justify-center px-6">
        <div className="max-w-lg space-y-4 text-center">
          <p className="text-2xl font-semibold text-white">Restricted area</p>
          <p className="text-gray-400">
            Your account is not in the admin allowlist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200 px-6 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Training Rules Configuration</h1>
          
          {/* Runbook link */}
          <a 
            href={RUNBOOK_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-400 hover:text-blue-300 hover:underline inline-flex items-center gap-1 mb-4"
          >
            📘 Training Framework v1 runbook – how to safely change rules and run regression checks
          </a>
          
          <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 text-amber-200 text-sm">
            <strong>⚠️ Caution:</strong> Changes here affect how new training plans are generated. 
            Use conservative adjustments and re-run the training framework regression scripts before deploying to production.
            <br /><br />
            <span className="text-amber-300/80">These rules affect newly generated training plans only. Existing schedules are not modified.</span>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 text-red-200 mb-6">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-400">Loading rules...</p>
          </div>
        )}

        {/* Rules list */}
        {!loading && (
          <div className="space-y-6">
            {RULE_DEFINITIONS.map(rule => {
              const state = ruleStates[rule.name] || {};
              const message = messages[rule.name];
              const isSaving = saving[rule.name];
              const hasChanges = state.editValue !== state.currentValue;
              const hasAdminOverride = state.adminValue !== undefined && state.adminValue !== null;
              const isUsingDefault = !hasAdminOverride;
              
              return (
                <div key={rule.name} className="bg-gray-900 border border-gray-800 rounded-lg p-5">
                  {/* Rule header */}
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{rule.label}</h3>
                      <p className="text-sm text-gray-400 mt-1">{rule.description}</p>
                      {/* Internal rule key */}
                      <p className="text-xs text-gray-600 mt-1 font-mono">rule_id: {rule.name}</p>
                    </div>
                    <button
                      onClick={() => handleShowHistory(rule.name)}
                      className="text-xs text-blue-400 hover:text-blue-300 hover:underline px-2 py-1 rounded bg-gray-800 border border-gray-700"
                    >
                      History ({state.historyCount || 0})
                    </button>
                  </div>
                  
                  {/* Current effective value & admin override status */}
                  <div className="mt-3 bg-gray-800/50 rounded p-3 text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300 font-semibold">Current effective:</span>
                      <span className="text-white font-medium">
                        {rule.type === 'bool' ? (
                          <span className={state.currentValue ? 'text-green-400' : 'text-red-400'}>
                            {state.currentValue ? '✅ Enabled' : '❌ Disabled'}
                          </span>
                        ) : (
                          formatDisplayValue(state.currentValue, rule)
                        )}
                      </span>
                      <span className="text-gray-500 italic">
                        {isUsingDefault ? '(developer default)' : '(admin override)'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300 font-semibold">Admin override:</span>
                      <span className={hasAdminOverride ? 'text-blue-400' : 'text-gray-500'}>
                        {hasAdminOverride ? formatDisplayValue(state.adminValue, rule) : 'none'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300 font-semibold">Last changed:</span>
                      <span className="text-gray-500 italic">
                        {state.lastChanged?.timestamp 
                          ? new Date(state.lastChanged.timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                          : '(developer default)'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Input control */}
                  <div className="mt-4">
                    <p className="text-sm text-gray-300 font-semibold mb-2">Set admin override:</p>
                    {rule.type === 'bool' ? (
                      <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <span className="text-gray-400 text-sm">New value:</span>
                          <input
                            type="checkbox"
                            checked={state.editValue ?? state.currentValue ?? true}
                            onChange={(e) => handleValueChange(rule.name, e.target.checked)}
                            className="w-5 h-5 rounded bg-gray-800 border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <span className="text-gray-300">
                            {(state.editValue ?? state.currentValue) ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                        {rule.isProtected && (
                          <p className="text-xs text-amber-400 mt-2">
                            🔒 Protected rule: Changing this requires typing <code className="bg-gray-800 px-1 rounded">DISABLE SAFETY</code> to confirm, plus full regression testing.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-400 text-sm">Value:</span>
                        <input
                          type="number"
                          min={rule.min * (rule.displayMultiplier || 1)}
                          max={rule.max * (rule.displayMultiplier || 1)}
                          step={rule.step * (rule.displayMultiplier || 1)}
                          value={((state.editValue ?? state.currentValue ?? rule.min) * (rule.displayMultiplier || 1)).toFixed(rule.type === 'int' ? 0 : 0)}
                          onChange={(e) => {
                            const rawValue = parseFloat(e.target.value) / (rule.displayMultiplier || 1);
                            handleValueChange(rule.name, rawValue);
                          }}
                          className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:border-blue-500 focus:outline-none"
                        />
                        <span className="text-gray-400 text-sm">
                          {rule.displaySuffix} <span className="text-gray-500">(allowed range: {(rule.min * (rule.displayMultiplier || 1)).toFixed(0)}–{(rule.max * (rule.displayMultiplier || 1)).toFixed(0)}{rule.displaySuffix})</span>
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Recommendation */}
                  {rule.recommendation && (
                    <p className="text-xs text-gray-500 mt-2">
                      💡 {rule.recommendation}
                    </p>
                  )}
                  
                  {/* Message */}
                  {message && (
                    <div className={`mt-3 text-sm ${message.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                      {message.text}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => handleSave(rule.name)}
                      disabled={isSaving || !hasChanges}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        hasChanges && !isSaving
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isSaving ? 'Validating...' : 'Validate & Save'}
                    </button>
                    {hasChanges && (
                      <button
                        onClick={() => handleValueChange(rule.name, state.currentValue)}
                        className="px-4 py-2 rounded text-sm font-medium bg-gray-700 hover:bg-gray-600 text-gray-300"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* History Modal */}
        {historyModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setHistoryModal(null)}>
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">
                  History: {RULE_DEFINITIONS.find(r => r.name === historyModal)?.label}
                </h3>
                <button onClick={() => setHistoryModal(null)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              
              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : historyData.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No history available</p>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {historyData.map((entry, i) => (
                    <div key={i} className="bg-gray-800 rounded p-3 text-sm">
                      <div className="flex justify-between text-gray-400">
                        <span>v{entry.version}</span>
                        <span>{entry.updated_at ? new Date(entry.updated_at).toLocaleString() : 'N/A'}</span>
                      </div>
                      <div className="text-white mt-1">
                        {entry.previous_value !== null && (
                          <span className="text-red-400 line-through mr-2">{JSON.stringify(entry.previous_value)}</span>
                        )}
                        <span className="text-green-400">{JSON.stringify(entry.rule_value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmation Modal for Protected Rules */}
        {confirmModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-red-700 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-white mb-2">⚠️ Protected Rule Change</h3>
              <p className="text-gray-300 text-sm mb-4">
                You are about to modify a critical safety rule. Disabling <strong>No Back-to-Back Hard Days</strong> significantly increases injury risk.
              </p>
              <p className="text-amber-300 text-sm mb-4">
                Before saving, you must:
                <br />• Create a new framework generation version (e.g., v2)
                <br />• Run full regression tests with the new setting
              </p>
              <p className="text-gray-400 text-sm mb-3">
                Type <code className="bg-gray-800 px-2 py-1 rounded text-red-400">DISABLE SAFETY</code> to confirm:
              </p>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:border-red-500 focus:outline-none mb-4"
                placeholder="Type DISABLE SAFETY"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmSave}
                  disabled={confirmInput !== 'DISABLE SAFETY'}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    confirmInput === 'DISABLE SAFETY'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue
                </button>
                <button
                  onClick={() => { setConfirmModal(null); setConfirmInput(''); }}
                  className="px-4 py-2 rounded text-sm font-medium bg-gray-700 hover:bg-gray-600 text-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Confirmation Modal (after validation passes) */}
        {saveConfirmModal && (() => {
          const rule = RULE_DEFINITIONS.find(r => r.name === saveConfirmModal);
          const state = ruleStates[saveConfirmModal] || {};
          return (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-white mb-4">Confirm Change</h3>
                <div className="bg-gray-800 rounded p-4 mb-4 text-sm">
                  <p className="text-gray-300 mb-2">
                    You are changing <strong className="text-white">{rule?.label}</strong>
                  </p>
                  <p className="text-gray-400 font-mono text-xs mb-2">({rule?.name})</p>
                  <p className="text-gray-300">
                    <span className="text-red-400">{formatDisplayValue(state.currentValue, rule)}</span>
                    <span className="text-gray-500 mx-2">→</span>
                    <span className="text-green-400">{formatDisplayValue(state.editValue, rule)}</span>
                  </p>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  This affects new training plans only (existing schedules will not change).
                </p>
                <p className="text-gray-400 text-sm mb-4">
                  Are you sure you want to save this override?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveConfirm}
                    className="px-4 py-2 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Confirm & Save
                  </button>
                  <button
                    onClick={() => setSaveConfirmModal(null)}
                    className="px-4 py-2 rounded text-sm font-medium bg-gray-700 hover:bg-gray-600 text-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default TrainingRulesAdmin;
